import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import i18n from "./setup/i18n";
import createSightVuetify from "./setup/vuetify";
import "@socotec.io/socio-vue-components/style.css";

import SocioManifest from "../socio-manifest.json";
import PackageJson from "../package.json";
import SocioVueComponents, { initializeTracingAndLogging } from "@socotec.io/socio-vue-components";
import { createOidc, useAuth, useOidcStore } from "vue3-oidc";
import { WebStorageStateStore } from "oidc-client-ts";
import { createORM } from "pinia-orm";
import { createPinia } from "pinia";
import * as Sentry from "@sentry/vue";
import { getDefaultSentryConfig } from "./setup/sentryConfig";
import { createSentryPiniaPlugin } from "@sentry/vue";
import { oidcSettings } from "./setup/OIDCSettings";

const MAX_SILENT_RENEW_ERRORS = 3;
const FATAL_SILENT_RENEW_ERRORS = new Set([
  "login_required",
  "interaction_required",
  "session_not_found",
]);

const faroSettings = {
  enableTelemetry: import.meta.env.VITE_APP_ENABLE_TELEMETRY,
  grafanaApiKey: import.meta.env.VITE_APP_GRAFANA_API_KEY,
  collectorUrl: import.meta.env.VITE_APP_COLLECTOR_URL,
  propagateUrls: import.meta.env.VITE_APP_PROPAGATE_URLS,
  grpcApiEnv: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  faroLogLevel: import.meta.env.VITE_APP_FARO_LOG_LEVEL,
};

initializeTracingAndLogging(SocioManifest, PackageJson, faroSettings, {
  router,
});

let silentRenewErrorCount = 0;

await createOidc({
  oidcSettings,
  auth: false,
  events: {
    addUserLoaded: (user) => {
      useOidcStore().actions.value.setUser(user);
      silentRenewErrorCount = 0;
      Sentry.setUser({ id: user.profile.usermanagementUuid, email: user.profile.email });
    },
    addSilentRenewError: (error) => {
      silentRenewErrorCount++;
      if (
        silentRenewErrorCount >= MAX_SILENT_RENEW_ERRORS ||
        FATAL_SILENT_RENEW_ERRORS.has(error?.error)
      ) {
        useAuth().signoutRedirect();
      }
    },
  },
  refreshToken: { enable: true },
  userStore: new WebStorageStateStore({ prefix: "vue3-oidc", store: globalThis.sessionStorage }),
});

const app = createApp(App);
Sentry.init(
  getDefaultSentryConfig({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    router,
    release: `sight@${import.meta.env.VITE_APP_VERSION}`,
    enabled: import.meta.env.MODE !== "development",
    environment: import.meta.env.VITE_APP_SOCIO_GRPC_API_ENV,
  }),
);

const pinia = createPinia().use(createORM());
pinia.use(createSentryPiniaPlugin());

app.use(router);
app.use(pinia);
app.use(i18n);
app.use(createSightVuetify());
app.use(SocioVueComponents, i18n);

app.mount("#app");
